import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { baseUrl } from "../utils/utils";

// Updated to accept pagination parameters
export const get_ticket_list = createAsyncThunk(
  "tickets/getTickets", 
  async ({ page, rows }) => {
    const body = new FormData();
    body.append("action", "get_tickets");
    body.append("page", page);
    body.append("limit", rows);
    
    const response = await fetch(baseUrl + "tickets/get_tickets.php", {
      method: "POST",
      body: body,
    });
    const res = await response.json();
    return res;
  }
);

// Optional: Add individual ticket fetch (similar to bank detail)
export const get_ticket_detail = createAsyncThunk(
  "tickets/getTicketDetail",
  async (ticketId) => {
    const body = new FormData();
    body.append("action", "get_ticket_detail");
    body.append("ticket_id", ticketId);
    
    const response = await fetch(baseUrl + "tickets/get_ticket_detail.php", {
      method: "POST",
      body: body,
    });
    const res = await response.json();
    return res;
  }
);

// Optional: Add ticket status update (similar to bank status update)
export const update_ticket_status = createAsyncThunk(
  "tickets/updateTicketStatus",
  async ({ ticketId, status }) => {
    const body = new FormData();
    body.append("action", "update_status");
    body.append("ticket_id", ticketId);
    body.append("status", status);
    
    const response = await fetch(baseUrl + "tickets/update_ticket.php", {
      method: "POST",
      body: body,
    });
    const res = await response.json();
    return res;
  }
);

const initialState = {
  ticket_list: [],
  total_ticket_count: 0, // Added for pagination
  loading: false,
  error: null,
  selectedTicket: null,
  ticketDetail: null, // Added for individual ticket details
  detailLoading: false, // Added for detail loading state
  updateLoading: false, // Added for update operations
};

const ticketSlice = createSlice({
  name: "tickets",
  initialState,
  reducers: {
    setSelectedTicket: (state, action) => {
      state.selectedTicket = action.payload;
    },
    clearSelectedTicket: (state) => {
      state.selectedTicket = null;
    },
    clearTicketDetail: (state) => {
      state.ticketDetail = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get ticket list cases
      .addCase(get_ticket_list.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(get_ticket_list.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(get_ticket_list.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        if (data.status === "success") {
          state.ticket_list = data.data || [];
          // Handle total count from API response
          state.total_ticket_count = data.totalcount || data.data?.length || 0;
        } else {
          state.ticket_list = [];
          state.total_ticket_count = 0;
          state.error = data.message || "Failed to fetch tickets";
        }
      })
      
      // Get ticket detail cases
      .addCase(get_ticket_detail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(get_ticket_detail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.error.message;
      })
      .addCase(get_ticket_detail.fulfilled, (state, action) => {
        state.detailLoading = false;
        const data = action.payload;
        if (data.status === "success") {
          state.ticketDetail = data.data;
        } else {
          state.ticketDetail = null;
          state.error = data.message || "Failed to fetch ticket details";
        }
      })
      
      // Update ticket status cases
      .addCase(update_ticket_status.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(update_ticket_status.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.error.message;
      })
      .addCase(update_ticket_status.fulfilled, (state, action) => {
        state.updateLoading = false;
        const data = action.payload;
        if (data.status === "success") {
          // Update the ticket in the list if it exists
          const ticketIndex = state.ticket_list.findIndex(
            ticket => ticket.ticketid === data.ticket_id
          );
          if (ticketIndex !== -1) {
            state.ticket_list[ticketIndex] = {
              ...state.ticket_list[ticketIndex],
              status: data.new_status,
            };
          }
          // Update detail if it's the same ticket
          if (state.ticketDetail && state.ticketDetail.ticketid === data.ticket_id) {
            state.ticketDetail = {
              ...state.ticketDetail,
              status: data.new_status,
            };
          }
        } else {
          state.error = data.message || "Failed to update ticket status";
        }
      });
  },
});

export const { 
  setSelectedTicket, 
  clearSelectedTicket, 
  clearTicketDetail, 
  clearError 
} = ticketSlice.actions;

export default ticketSlice.reducer;